import _                                                                                                                                                                       from 'underscore';
import { v4 as uuid }                                                                                                                                                          from 'uuid';
import moment, { Moment }                                                                                                                                                      from 'moment';
import { TimeService }                                                                                                                                                         from './TimeService';
import { CorvalBusinessFunction, CorvalProject, CorvalProjectActivity, CorvalProjectVersion, CorvalProjectVersionMilestone, CorvalStaticProjectActivity, CorvalUIColor, Uuid } from '../types';

import {
    ACTIVITY_CRITICALITY_INFO,
    ACTIVITY_STATUS_INFO,
    ActivityCriticalityType,
    ActivityStatusType,
    ENTITY_COLORIZATION,
    ENTITY_ICONS,
    EnumDescriptorType,
    ObjectiveStatusType,
    OBJECTIVE_STATUS_INFO,
    SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD,
    TimelineDataObjectType
}                                                        from '@src/constants';
import { SemanticICONS }                                 from "semantic-ui-react";
import { findTopSimilarityString, getCSSVar, hexToRGBa } from "@src/util";
import classNames                                        from "classnames";


export type ProjectDateFieldObject = {
    fieldName: string,
    dependency: any,
    label: string,
    description: string,
}

export type ProjectTimelineDateFieldObject = {
    id: Uuid,
    fieldId: Uuid,
    label: string,
    description: string,
    value: string,
    rawValue: string,
    order: number,
    type: TimelineDataObjectType,
    color: CorvalUIColor,
    icon: SemanticICONS,
}

export class ProjectService {

    // Gets a project version object by default criteria, usually the initial or earliest-editable project version.
    static getDefaultProjectVersion = ( projectVersions: Partial<CorvalProjectVersion>[] ): Partial<CorvalProjectVersion> => {

        if( _.isEmpty( projectVersions ) ) return null;

        return _.find( projectVersions, ( version ) => { return version?.isDefaultVersion === true; } ) ?? null;
    };

    static getActiveProjectVersion = ( projectVersions: Partial<CorvalProjectVersion>[] ): Partial<CorvalProjectVersion> => {

        if( _.isEmpty( projectVersions ) ) return null;

        return _.find( projectVersions, ( version ) => { return version?.isActiveVersion === true; } ) ?? null;
    };

    // Gets a project version object by id, or returns the default version if not found; Requires a project version array to be present.
    static getProjectVersionById = ( projectVersions: Partial<CorvalProjectVersion>[], projectVersionId: Uuid, fallbackToDefaultVersion = true ): Partial<CorvalProjectVersion> => {

        const version          = _.findWhere( projectVersions, { id: projectVersionId } ),
              fallbackResponse = fallbackToDefaultVersion ? ProjectService.getDefaultProjectVersion( projectVersions ) : null;

        return version ?? fallbackResponse;

    };

    static getMinMaxActivityDates = ( projectActivities: CorvalProjectActivity[] ) => {

        let dates: Moment[] = [];

        projectActivities.map( a => {

            if( a.startTimeUtc && a.endTimeUtc ){

                dates.push( TimeService.ISOtoMoment( a.startTimeUtc ) );
                dates.push( TimeService.ISOtoMoment( a.endTimeUtc ) );
            }
        } );

        let earliestDate = moment.min( dates ),
            latestDate   = moment.max( dates );

        return { earliestDate, latestDate };
    };

    static getMapTimeframeFromStart = ( startDate: Moment, maxQuarters = 4 ): [ mapStartDate: Moment, mapEndDate: Moment ] => {

        // update default
        let mapStartDate = startDate.clone().startOf( 'quarter' );
        let mapEndDate = mapStartDate.clone().add( maxQuarters - 1, 'quarters' ).endOf( 'quarter' );

        return [
            mapStartDate,
            mapEndDate
        ];
    };

    static getProjectMilestones = ( entityLikeObject: Partial<CorvalProject | CorvalProjectVersion | CorvalStaticProjectActivity> = {}, projectVersionId = null ) => {

        let projectMilestones = [];

        // Is this a top-level project object?
        if( _.get( entityLikeObject, 'projectVersions' ) ){

            projectMilestones = _.chain( entityLikeObject as CorvalProject )
                                 .get( 'projectVersions' )
                                 .filter( ( projectVersion: CorvalProjectVersion ) => ( projectVersionId ? ( projectVersion?.id === projectVersionId ) : true ) )
                                 .map( version => _.get( version, 'projectVersionMilestones' ) )
                                 .union()
                                 .flatten()
                                 .reject( ( milestone: CorvalProjectVersionMilestone ) => ( projectVersionId && ( milestone.projectVersionId === projectVersionId ) ) )
                                 .value();
        }

        // Is this a project version object?
        if( _.get( entityLikeObject, 'projectVersionMilestones' ) ){

            projectMilestones = _.chain( entityLikeObject as CorvalProjectVersion )
                                 .get( 'projectVersionMilestones' )
                                 .union()
                                 .flatten()
                                 .reject( ( milestone: CorvalProjectVersionMilestone ) => ( projectVersionId && ( milestone.projectVersionId === projectVersionId ) ) )
                                 .value()
        }

        // Is this a static project activity object?
        if( _.get( entityLikeObject, 'milestoneRelationships' ) ){

            projectMilestones = _.chain( entityLikeObject as CorvalStaticProjectActivity )
                                 .get( 'milestoneRelationships' )
                                 .map( relationship => _.get( relationship, 'projectVersionMilestone' ) )
                                 .union()
                                 .flatten()
                                 .value()
        }

        return projectMilestones;
    };

    static hasProjectMilestones = ( projectVersionLikeObject: Partial<CorvalProject | CorvalProjectVersion | CorvalStaticProjectActivity> = {}, projectVersionId = null ) => {

        const milestones = ProjectService.getProjectMilestones( projectVersionLikeObject, projectVersionId );

        return milestones ? !_.isEmpty( milestones ) : false;
    };

    static isTimelineDataObjectAProjectMilestones = ( timelineDataObject: Partial<ProjectTimelineDateFieldObject> = {} ) => {

        return ( timelineDataObject?.type === TimelineDataObjectType.PROJECT_MILESTONE )
    };

    static createTimelineDataObject = ( data: Partial<ProjectTimelineDateFieldObject> ) => {

        const _id       = uuid(),
              _date     = TimeService.momentToLocalDate( moment() ),
              _rawValue = TimeService.asDisplayDate( moment() );

        return {
            id:          _id,
            fieldId:     _id,
            order:       -1,
            label:       `General Date (${ _date })`,
            description: '[ No description ]',
            rawValue:    _rawValue,
            value:       TimeService.localDateToDisplayDate( data.value ?? _date ),
            type:        TimelineDataObjectType.GENERIC_DATE,
            color:       ENTITY_COLORIZATION.MILESTONES.DEFAULT,
            icon:        ENTITY_ICONS.MILESTONES.ITEM,

            ...data

        } as ProjectTimelineDateFieldObject
    }

    static createProjectMilestoneTimelineDataObject = ( data: Partial<ProjectTimelineDateFieldObject> ) => {
        return {
            ...ProjectService.createTimelineDataObject( data ),
            type:  TimelineDataObjectType.PROJECT_MILESTONE,
            color: data.color ?? ENTITY_COLORIZATION.MILESTONES.DEFAULT,
            icon:  data.icon ?? ENTITY_ICONS.MILESTONES.ITEM,
        } as ProjectTimelineDateFieldObject
    }

    static getMilestoneVisualProperties = ( icon?: string, color?: string ) => {

        const hasIcon   = !_.isEmpty( icon ),
              hasColor  = !_.isEmpty( color ),
              iconName  = hasIcon ? icon : ENTITY_ICONS.MILESTONES.ITEM,
              iconColor = hasColor ? color : ENTITY_COLORIZATION.MILESTONES.DEFAULT;

        const iconClasses = classNames( [ iconName, iconColor ], {
            'rotate--45': !hasIcon,
        } )

        const cssValue = getCSSVar( `--color-${ iconColor }` );

        const alphaHexColor = cssValue ? hexToRGBa( cssValue, 0.25 ) : 'none';

        // Create event models
        return {
            hasIcon,
            hasColor,
            iconName,
            iconColor,
            cssValue,
            alphaHexColor,
            iconClasses
        };

    }

    static getTimelineDataObjectFromProject = ( entityLikeObject: Partial<CorvalProject | CorvalProjectVersion | CorvalStaticProjectActivity>, projectVersionId = null ): ProjectTimelineDateFieldObject[] => {

        /* Project timeline field objects */
        let orderedFields: ProjectTimelineDateFieldObject[] = [];

        /* Timeline field objects */
        ProjectService.getProjectMilestones( entityLikeObject, projectVersionId ).map( ( milestone: CorvalProjectVersionMilestone, index ) => {

            orderedFields.push( ProjectService.createProjectMilestoneTimelineDataObject( {
                id:          milestone.id,
                fieldId:     milestone.id,
                order:       index,
                label:       milestone.name,
                description: milestone.description,
                value:       TimeService.localDateToDisplayDate( milestone.milestoneDate ),
                rawValue:    milestone.milestoneDate,
                color:       milestone?.colorValue as CorvalUIColor,
                icon:        milestone?.iconName as SemanticICONS,
            } ) );
        } );

        // sort, set order
        orderedFields = _.chain( orderedFields )
                         .sortBy( item => TimeService.asYearFirstDisplayDate( TimeService.localDateToMoment( item.rawValue ) ) )
                         .map( ( field, index ) => ( { ...field, order: index } ) )
                         .value();

        return orderedFields;
    };

    static getTimelineDataObjectFromSet = ( fieldId: string, fieldObjects: ProjectTimelineDateFieldObject[] ): ProjectTimelineDateFieldObject => {

        return _.findWhere( fieldObjects, { fieldId } ) ?? null;
    };

    static timelineDataObjectIsProjectMilestone = ( item: ProjectTimelineDateFieldObject | { type: TimelineDataObjectType } ) => {

        return ( item.type === TimelineDataObjectType.PROJECT_MILESTONE );
    };

    static getTimelineDataVisualDataFromProject = (
        projectVersionLikeObject: Partial<CorvalProject | CorvalProjectVersion>,
        projectVersionId        = null,
        _startDate: Moment      = moment(),
        _endDate: Moment        = moment(),
        timelineFieldRangePairs = Array<[ fieldIdStart: Uuid, fieldIdEnd: Uuid ]>
    ) => {

        let orderedFieldHash = ProjectService.getTimelineDataObjectFromProject( projectVersionLikeObject, projectVersionId ),
            fieldRowCount    = _.keys( orderedFieldHash ).length,
            minDate          = moment.min( _.pluck( orderedFieldHash, 'value' ).map( item => TimeService.displayDateToMoment( item ).startOf( 'quarter' ) ) ),
            maxDate          = moment.max( _.pluck( orderedFieldHash, 'value' ).map( item => TimeService.displayDateToMoment( item ).endOf( 'quarter' ) ) );

        let startDate = moment.min( [ minDate, _startDate ] ),
            endDate   = moment.max( [ maxDate, _endDate ] );

        let yearDiff            = endDate.diff( startDate, 'years' ),
            displayLabels       = ( yearDiff <= 12 ),
            monthLabelFormatter = ( yearDiff > 8 ) ? ( m: Moment ) => m.format( 'MMM' )[ 0 ] : null;

        /* Setup grid config */
        let columnInfo         = {
                gridColumns: TimeService.getMonthDifferential( startDate, endDate, ),
                gridRows:    fieldRowCount * 2,
            },
            fieldCellWidth     = 7,
            fieldDateElements  = {},
            fieldRangeElements = {}; // @TODO: coordinate with timelineFieldRangePairs


        orderedFieldHash.map( ( { fieldId, value, rawValue, label }, index ) => {

            let fieldDate             = TimeService.localDateToMoment( rawValue ),
                fieldColumnStart      = TimeService.getMonthDifferential( startDate, fieldDate ) + 1,
                fieldRow              = ( index * 2 ) + 2,
                fieldAlterationStyles = {};

            fieldDateElements[ fieldId ] = {
                label:        label,
                fieldDate:    fieldDate,
                top:          fieldRow,
                start:        fieldColumnStart,
                style:        fieldAlterationStyles,
                width:        fieldCellWidth,
                isContinuous: true,
            }

        } );

        return {
            timelineDataObjects: orderedFieldHash,
            visualData:          {
                columnInfo,
                labelInfo: {
                    displayLabels,
                    monthLabelFormatter,
                },
                timeInfo:  {
                    startDate,
                    endDate,
                    minDate,
                    maxDate,
                },
                fieldInfo: {
                    fieldRowCount,
                    fieldDateElements,
                    fieldRangeElements,
                    fieldCellWidth,
                },
            }
        };
    }

    static getBusinessFunctionsFromProjects = ( projects: Array<CorvalProject> ) => {

        const allBusinessFunctions = _.chain( projects )
                                      .map( project => project.businessFunctions ).flatten()
                                      .uniq( ( businessFunction: CorvalBusinessFunction ) => businessFunction.id )
                                      .value();

        return allBusinessFunctions;
    };

    static getActivityCriticalityInfoFromType = ( criticalityType: ActivityCriticalityType, attribute: keyof EnumDescriptorType = null ): ( EnumDescriptorType | EnumDescriptorType[ keyof EnumDescriptorType ] | null ) => {
        let info = ACTIVITY_CRITICALITY_INFO[ criticalityType ] ?? null;
        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static getObjectiveStatusInfoFromType = ( statusType: ObjectiveStatusType, attribute: keyof EnumDescriptorType = null ): ( EnumDescriptorType | EnumDescriptorType[ keyof EnumDescriptorType ] | null ) => {

        let info = OBJECTIVE_STATUS_INFO[ statusType ] ?? null;

        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static getActivityStatusInfoFromType = ( statusType: ActivityStatusType, attribute: keyof EnumDescriptorType = null ): ( EnumDescriptorType | EnumDescriptorType[ keyof EnumDescriptorType ] | null ) => {

        let info = ACTIVITY_STATUS_INFO[ statusType ] ?? null;

        return ( info ) ? attribute ? info[ attribute ] : info : null;
    };

    static isMedicalAffairs = ( businessFunction: Partial<CorvalBusinessFunction> ): boolean => {

        const maxScoreThreshold = SYSTEM_STRING_SIMILARITY_DEFAULT_THRESHOLD;
        const stringsToCompare = [
            'medicalAffairs',
            'medical affairs',
            'medAffairs',
            'med affairs',
            'MEDICALAFFAIRS',
            'MEDAFFAIRS',
            'MEDICAL_AFFAIRS',
            'MED_AFFAIRS'
        ]

        const maxResultMatchForName         = businessFunction?.name ? findTopSimilarityString( businessFunction?.name, stringsToCompare, maxScoreThreshold ) : null,
              maxResultMatchForResourceCode = businessFunction?.resourceCode ? findTopSimilarityString( businessFunction?.name, stringsToCompare, maxScoreThreshold ) : null;

        const result = _.chain( [ maxResultMatchForName, maxResultMatchForResourceCode ] )
                        .map( result => result ? ( result.score > maxScoreThreshold ) : false )
                        .some( result => result === true )
                        .value()

        return result;
    };

    static containsMedicalAffairBusinessFunction = ( businessFunctionTypes: CorvalBusinessFunction[] = [] ): boolean => {

        const collectionResults = businessFunctionTypes.map( businessFunctionType => ProjectService.isMedicalAffairs( businessFunctionType ) )

        return _.some( collectionResults, true );
    };
}
